import React, { useState, useEffect, useContext } from "react";
import {
  Row,
  Col,
  Button,
  Tag,
  message,
  Typography,
  Descriptions,
  Spin,
  Result,
  List,
  Tabs,
} from "antd";
import Text from "antd/lib/typography/Text";
import {
  ShoppingCartOutlined,
  InboxOutlined,
  CarOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  UserOutlined,
  PhoneOutlined,
  EnvironmentOutlined,
} from "@ant-design/icons";

import "./index.css";
import { DateFormat } from "../../helpers";
import { opsApiCall, getOpsToken } from "../../helpers/rapsapOpsAuth";
import CardContainer from "../../components/Card";
import TableContainer from "../../components/Table";
import Sidebar from "../../components/Sidebar";
import OrderSidebar from "../Orders/OrderSidebar";
import { StoreContext } from "../../context";
import { useDeliveryStats } from "../../hooks/useDeliveryStats";
import DeliveryStatsCards from "../../components/DeliveryStatsCards";

export default function Delivery() {
  const { storeId } = useContext(StoreContext);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentFilter, setCurrentFilter] = useState("out_for_delivery");
  const [packagesData, setPackagesData] = useState([]);
  const [total, setTotal] = useState(0);
  const [sidebarVisible, setSidebarVisible] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);

  // Order sidebar state
  const [orderSidebar, setOrderSidebar] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState(0);

  // Use the custom hook
  const {
    deliveryData,
    loading: statsLoading,
    fetchStats,
  } = useDeliveryStats();

  const [savedPayload, setSavedPayload] = useState({});
  useEffect(() => {
    initializeAuth();
  }, [storeId]);

  const initializeAuth = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = await getOpsToken();
      if (token) {
        await Promise.all([fetchStats(), getPackagesData()]);
      } else {
        throw new Error("Failed to authenticate");
      }
    } catch (error) {
      console.error("Error initializing auth:", error);
      setError("Failed to initialize authentication. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const getPackagesData = async (payload = {}) => {
    try {
      let packagePayload = {
        filter: payload.filter || currentFilter,
        page: payload.page || 0,
        size: payload.size || 12,
        store_id: storeId || "1",
        type: "admin",
        ...payload,
      };

      const res = await opsApiCall("/rapid/getPackages", packagePayload);

      if (Array.isArray(res?.data)) {
        setPackagesData(res.data);
        setTotal(res.meta?.total || res.data.length);
      }
      setSavedPayload(payload);
    } catch (error) {
      console.error("Error fetching packages data:", error);
    }
  };

  const handleViewPackage = (record) => {
    setSelectedPackage(record);
    setSidebarVisible(true);
  };

  // Handle row click to show order details
  const handleRowClick = (record) => {
    console.log("Row clicked with record:", record);
    setSelectedOrderId(record.order_id);
    setOrderSidebar(true);
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "delivered":
        return "green";
      case "out_for_delivery":
        return "blue";
      case "packed":
        return "orange";
      case "paid":
        return "cyan";
      case "reschedule":
        return "red";
      case "arrived":
        return "purple";
      default:
        return "default";
    }
  };

  const getStatusText = (status) => {
    switch (status?.toLowerCase()) {
      case "out_for_delivery":
        return "Out For Delivery";
      case "reschedule":
        return "Reschedule";
      default:
        return status?.charAt(0).toUpperCase() + status?.slice(1) || "N/A";
    }
  };

  // Handle filter change
  const handleFilterChange = (filter) => {
    setCurrentFilter(filter);
    setLoading(true);
    getPackagesData({ filter, page: 0 }).finally(() => setLoading(false));
  };

  // Helper function to get delivery staff info from packageLogs
  const getDeliveryStaffInfo = (packageLogs) => {
    if (!packageLogs || packageLogs.length === 0)
      return { name: "N/A", date: null };

    // Find the log entry for 'delivered' or 'out_for_delivery' status
    const deliveryLog = packageLogs.find(
      (log) =>
        log.order_status === "delivered" ||
        log.order_status === "out_for_delivery"
    );

    if (deliveryLog) {
      return {
        name: deliveryLog.handled_by || "N/A",
        date: deliveryLog.created_at,
      };
    }

    return { name: "N/A", date: null };
  };

  const columns = [
    {
      title: "Order Id",
      dataIndex: "order_id",
      key: "order_id",
      width: 100,
      sorter: true,
    },
    {
      title: "Package Id",
      dataIndex: "package_id",
      key: "package_id",
      width: 100,
      sorter: true,
    },
    {
      title: "Store",
      dataIndex: "store_name",
      key: "store_name",
      width: 120,
    },
    // {
    //   title: "Name",
    //   key: "name",
    //   width: 150,
    //   render: (text, record) => record.delivery_address?.name || "N/A",
    // },
    // {
    //   title: "Phone",
    //   key: "phone",
    //   width: 130,
    //   render: (text, record) => record.delivery_address?.phone || "N/A",
    // },
    {
      title: (
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          Status
          <select
            value={currentFilter}
            onChange={(e) => handleFilterChange(e.target.value)}
            style={{
              padding: "2px 6px",
              border: "1px solid #d9d9d9",
              borderRadius: "4px",
              fontSize: "12px",
              backgroundColor: "#fff",
            }}
          >
            <option value="out_for_delivery">Out For Delivery</option>
            <option value="delivered">Delivered</option>
            <option value="arrived">Arrived</option>
          </select>
        </div>
      ),
      dataIndex: "order_status",
      key: "order_status",
      width: 180,
      render: (status) => (
        <Tag color={getStatusColor(status)}>{getStatusText(status)}</Tag>
      ),
    },
    {
      title: "Delivery Staff",
      dataIndex: "staff_name",
      key: "staff_name",
      width: 120,
      render: (text) => text || "N/A",
    },
    {
      title: "Amount",
      dataIndex: "grand_total",
      key: "grand_total",
      width: 100,
      render: (text) => `₹${text || 0}`,
    },
    {
      title: "Date",
      dataIndex: "created_at",
      key: "created_at",
      width: 150,
      render: (text) => DateFormat(text),
    },
  ];

  // Package Details Component
  function PackageDetails({ packageData }) {
    const [activeTab, setActiveTab] = useState("details");

    if (!packageData) return null;

    return (
      <div style={{ padding: "16px 0" }}>
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <Tabs.TabPane tab="Package Details" key="details">
            <Descriptions
              title="Package Information"
              bordered
              column={1}
              size="small"
              style={{ marginBottom: "24px" }}
            >
              <Descriptions.Item label="Order ID">
                {packageData.order_id}
              </Descriptions.Item>
              <Descriptions.Item label="Package ID">
                {packageData.package_id}
              </Descriptions.Item>
              <Descriptions.Item label="Store">
                {packageData.store_name}
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                <Tag color={getStatusColor(packageData.order_status)}>
                  {getStatusText(packageData.order_status)}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Delivery Staff">
                <UserOutlined style={{ marginRight: 8 }} />
                {packageData.staff_name || "N/A"}
              </Descriptions.Item>
              <Descriptions.Item label="Total Amount">
                ₹{packageData.grand_total || 0}
              </Descriptions.Item>
              <Descriptions.Item label="Created Date">
                {DateFormat(packageData.created_at)}
              </Descriptions.Item>
            </Descriptions>

            {/* Customer Delivery Address */}
            {packageData.delivery_address && (
              <Descriptions
                title="Customer Delivery Address"
                bordered
                size="small"
                column={1}
                style={{ marginBottom: "24px" }}
              >
                <Descriptions.Item label="Name">
                  <UserOutlined style={{ marginRight: 8 }} />
                  {packageData.delivery_address.name}
                </Descriptions.Item>
                <Descriptions.Item label="Phone">
                  <PhoneOutlined style={{ marginRight: 8 }} />
                  {packageData.delivery_address.phone}
                </Descriptions.Item>
                <Descriptions.Item label="Address">
                  <EnvironmentOutlined style={{ marginRight: 8 }} />
                  {packageData.delivery_address.address1}
                  {packageData.delivery_address.address2 &&
                    `, ${packageData.delivery_address.address2}`}
                </Descriptions.Item>
                <Descriptions.Item label="City">
                  {packageData.delivery_address.city},{" "}
                  {packageData.delivery_address.state} -{" "}
                  {packageData.delivery_address.pincode}
                </Descriptions.Item>
              </Descriptions>
            )}
          </Tabs.TabPane>

          <Tabs.TabPane tab="Package Items" key="items">
            <List
              dataSource={packageData.packageDetails || []}
              renderItem={(item, index) => (
                <List.Item key={index}>
                  <List.Item.Meta
                    avatar={<InboxOutlined />}
                    title={item.product_name}
                    description={
                      <div>
                        <Text type="secondary">
                          Category: {item.category_name || "N/A"}
                        </Text>
                        <br />
                        <Text>
                          Quantity: {item.quantity}
                          {item.packed_quantity &&
                            ` | Packed: ${item.packed_quantity}`}
                        </Text>
                        <br />
                        <Tag color={getStatusColor(item.product_status)}>
                          {getStatusText(item.product_status)}
                        </Tag>
                      </div>
                    }
                  />
                </List.Item>
              )}
            />
          </Tabs.TabPane>

          <Tabs.TabPane tab="Package History" key="history">
            <div style={{ display: "flex", flexDirection: "column" }}>
              <div className="cnt-nav">
                <div className="lst-itms">
                  <ul className="lst">
                    {(packageData.packageLogs || []).map((log, index) => {
                      let statusText = "";
                      const status = log.order_status;

                      if (status === "packed") {
                        statusText = "Package Packed";
                      } else if (status === "out_for_delivery") {
                        statusText = "Out for Delivery";
                      } else if (status === "delivered") {
                        statusText = "Package Delivered";
                      } else {
                        statusText = status
                          ? status
                              .replace(/_/g, " ")
                              .replace(/\b\w/g, (l) => l.toUpperCase())
                          : "Unknown Status";
                      }

                      return (
                        <li
                          key={index}
                          className={
                            packageData.packageLogs.length > index + 1
                              ? "itm compltd"
                              : "itm activ"
                          }
                        >
                          <div>
                            <Text strong>{statusText}</Text>
                            <br />
                            <Text style={{ fontSize: 12, color: "#666" }}>
                              {DateFormat(log.created_at)}
                            </Text>
                            {log.handled_by && (
                              <>
                                <br />
                                <Text
                                  style={{ fontSize: 11, color: "#1890ff" }}
                                >
                                  Handled by: {log.handled_by}
                                </Text>
                              </>
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </div>
            </div>
          </Tabs.TabPane>
        </Tabs>
      </div>
    );
  }

  // Error state
  if (error && !loading) {
    return (
      <Result
        status="error"
        title="Failed to Load Delivery Data"
        subTitle={error}
        extra={[
          <Button type="primary" key="retry" onClick={initializeAuth}>
            Retry
          </Button>,
        ]}
      />
    );
  }

  // Loading state
  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "50px" }}>
        <Spin size="large" />
        <div style={{ marginTop: "16px" }}>Loading delivery data...</div>
      </div>
    );
  }

  // Success state - render components only after data is loaded
  return (
    <div className="delivery-container">
      {/* Use the reusable component */}
      <DeliveryStatsCards
        deliveryData={deliveryData}
        showOnly={["new-order", "out-for-delivery", "delivered"]}
      />

      {/* Delivery Packages Table */}
      <CardContainer hoverable={false}>
        <TableContainer
          breadcrumbTitle="Delivery Management"
          loading={loading}
          columns={columns}
          data={packagesData}
          total={total}
          searchFor="Order ID, Package ID, Customer Name"
          functionsHandler={getPackagesData}
          rowClickHandler={handleRowClick}
          addButton={false}
        />
      </CardContainer>

      {/* Order Details Sidebar - Uses the same OrderSidebar from Orders page */}
      {orderSidebar && (
        <OrderSidebar
          visible={orderSidebar}
          onClose={() => setOrderSidebar(false)}
          order_id={selectedOrderId}
        />
      )}

      {/* Package Details Sidebar (keeping the original one as well) */}
      <Sidebar
        title="Package Details"
        visible={sidebarVisible}
        onClose={() => {
          setSidebarVisible(false);
          setSelectedPackage(null);
        }}
        width={600}
      >
        <PackageDetails packageData={selectedPackage} />
      </Sidebar>
    </div>
  );
}
